<script setup lang="ts">
import { formRules } from '@/helpers/validators'
import type { ProjektFeld } from '@/models/apollon_conf_fk/ProjektFeld'
import { LookupValue, LookupValueCollection } from '@/models/LookupValue'
import dayjs, { type Dayjs } from 'dayjs'
import { computed, onMounted, ref, type Ref, watch } from 'vue'
import FieldInfoIcon from '@/components/field-info-icon.vue'

const modelValue = defineModel<string | number | Dayjs | object | null | undefined>('modelValue')

const {
    modelValueAsObject = false,
    readOnly = false,
    projektfeld = null,
    filter = null,
    hideOnEmpty = false,
} = defineProps<{
    modelValueAsObject?: boolean
    readOnly: boolean
    projektfeld: ProjektFeld
    filter?: { property: string; value: string | number | null }[] | null
    hideOnEmpty?: boolean
}>()

const rules = formRules

const datemenu = ref(false)
const loading = ref(false)
const wertelisteItems: Ref<LookupValue[]> = ref([])
const linkItems: Ref<LookupValue[]> = ref([])

const showInfoIcon = computed(() => {
    if (projektfeld?.ist_werteliste) {
        return !hideOnEmpty || wertelisteItems.value.length > 0
    }
    if (projektfeld?.ist_link) {
        return !hideOnEmpty || linkItems.value.length > 0
    }
    return true
})

// fieldModel(newVal) {
//     $emit('input', newVal);
// },
// value(newVal) {
//     fieldModel = newVal;
// },

watch(
    () => filter,
    (
        newVal: { property: string; value: string | number | null }[] | null,
        oldVal: { property: string; value: string | number | null }[] | null,
    ) => {
        if (newVal !== oldVal && newVal !== null) {
            loadSelectFieldItems(newVal)
        }
    },
)

onMounted(() => {
    loadSelectFieldItems(filter || [])
})

const emit = defineEmits<{
    (e: 'update:model-value', value: string | number | Dayjs | object | null | undefined): void
}>()

async function loadSelectFieldItems(filter: { property: string; value: string | number | null }[]) {
    filter = filter || []
    if (projektfeld?.ist_werteliste) {
        loading.value = true
        try {
            const wertelisteCollection = new LookupValueCollection()
            await wertelisteCollection.queryValue({
                apiCancelToken: projektfeld.feld_name,
                table: projektfeld.werteliste_tabelle,
                filter: JSON.stringify(filter),
                sort: 'reihenfolge',
            })
            wertelisteItems.value = wertelisteCollection.getModels()
        } catch (error) {
            console.error(error)
        } finally {
            loading.value = false
        }
    } else if (projektfeld?.ist_link) {
        loading.value = true
        try {
            if (projektfeld.linktabelle_produktkategorie_id) {
                filter.push({
                    property: 'produkt_kategorie_id',
                    value: projektfeld.linktabelle_produktkategorie_id,
                })
            }
            const linkCollection = new LookupValueCollection()
            await linkCollection.queryValue({
                apiCancelToken: projektfeld.feld_name,
                table: projektfeld.link_tabellenname,
                filter: JSON.stringify(filter),
            })
            linkItems.value = linkCollection.getModels()
        } catch (error) {
            console.error(error)
        } finally {
            loading.value = false
        }
    }
}

function formatDate(value: string | number | object | Dayjs | null | undefined) {
    if (!value) { return ''; }
    const raw = Array.isArray(value) ? value[0] : value
    if (!raw) { return ''; }
    const parsed = dayjs(raw as string | number | Date | Dayjs)
    return parsed.isValid() ? parsed.format('DD.MM.YYYY') : ''
}

function valueChanged(value: string | number | Dayjs | null | undefined | object) {
    datemenu.value = false
    const raw = Array.isArray(value) ? value[0] : value
    emit('update:model-value', raw)
}
</script>

<template>
    <div>
        <div class="text-subtitle-1 text-medium-emphasis">
            {{ projektfeld?.label || '' }}
            <span v-if="showInfoIcon"
                ><FieldInfoIcon :infotext="projektfeld?.hilfetext || null"></FieldInfoIcon
            ></span>
        </div>
        <v-text-field
            v-if="projektfeld?.db_type === 'string'"
            class="inputfield"
            :model-value="modelValue"
            type="text"
            :required="projektfeld.required"
            :readonly="readOnly"
            @update:model-value="valueChanged"
        />
        <v-textarea
            v-if="projektfeld?.db_type === 'text'"
            class="inputfield"
            :model-value="modelValue"
            :required="projektfeld.required"
            :readonly="readOnly"
            @update:model-value="valueChanged"
        />
        <v-switch
            v-if="projektfeld?.db_type === 'boolean'"
            class="inputfield"
            :model-value="modelValue"
            :readonly="readOnly"
            @update:model-value="valueChanged"
        ></v-switch>
        <v-number-input
            v-if="
                projektfeld?.db_type === 'integer' &&
                !projektfeld?.ist_link &&
                !projektfeld?.ist_werteliste
            "
            class="inputfield"
            :model-value="Number.parseInt(modelValue as string, 10)"
            :fieldname="projektfeld.feld_name"
            :rules="[
                rules.between(
                    projektfeld.value_from,
                    projektfeld.value_to,
                    projektfeld.warnmeldung,
                ),
                rules.isInt(),
            ]"
            :required="projektfeld.required"
            :readonly="readOnly"
            @update:model-value="valueChanged"
        />
        <v-autocomplete
            v-if="
                projektfeld?.db_type === 'integer' &&
                projektfeld?.ist_werteliste &&
                (!hideOnEmpty || wertelisteItems.length > 0)
            "
            class="inputfield"
            :clearable="!readOnly"
            clear-icon="fas fa-times"
            :model-value="modelValue"
            :loading="loading"
            item-title="value"
            item-value="id"
            :items="wertelisteItems"
            :required="projektfeld.required"
            :readonly="readOnly"
            :return-object="modelValueAsObject"
            @update:model-value="valueChanged"
        ></v-autocomplete>
        <v-autocomplete
            v-if="
                projektfeld?.db_type === 'integer' &&
                projektfeld?.ist_link &&
                (!hideOnEmpty || linkItems.length > 0)
            "
            class="inputfield"
            :clearable="!readOnly"
            clear-icon="fas fa-times"
            :model-value="modelValue"
            :loading="loading"
            item-title="value"
            item-value="id"
            :items="linkItems"
            :required="projektfeld.required"
            :readonly="readOnly"
            :return-object="modelValueAsObject"
            @update:model-value="valueChanged"
        ></v-autocomplete>
        <v-number-input
            v-if="projektfeld?.db_type === 'float'"
            class="inputfield"
            :model-value="Number.parseFloat(modelValue as string)"
            :rules="[
                rules.between(
                    projektfeld.value_from,
                    projektfeld.value_to,
                    projektfeld.warnmeldung,
                ),
            ]"
            :required="projektfeld.required"
            :fieldname="projektfeld.feld_name"
            :readonly="readOnly"
            @update:model-value="valueChanged"
        />
        <v-select
            v-if="projektfeld?.db_type === 'ja_nein_teilweise'"
            class="inputfield"
            :clearable="!readOnly"
            clear-icon="fas fa-times"
            :required="projektfeld.required"
            :model-value="modelValue as string"
            :readonly="readOnly"
            :items="['ja', 'nein', 'teilweise']"
            @update:model-value="valueChanged"
        >
        </v-select>
        <v-select
            v-if="projektfeld?.db_type === 'ja_nein_unknown'"
            class="inputfield"
            :clearable="!readOnly"
            clear-icon="fas fa-times"
            :required="projektfeld.required"
            :model-value="modelValue as string"
            :items="['ja', 'nein', 'unbekannt']"
            :readonly="readOnly"
            @update:model-value="valueChanged"
        >
        </v-select>
        <v-menu
            v-if="projektfeld?.db_type === 'date'"
            v-model:model-value="datemenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            :readonly="readOnly"
            max-width="290px"
            min-width="290px"
        >
            <template v-slot:activator="{ props }">
                <v-text-field
                    class="inputfield"
                    :clearable="!readOnly"
                    clear-icon="fas fa-times"
                    :model-value="formatDate(modelValue)"
                    :required="projektfeld.required"
                    readonly
                    v-bind="props"
                    @click:clear="valueChanged(null)"
                >
                    <template v-slot:prepend-inner>
                        <v-icon class="icon-margin" size="x-small">fas fa-calendar</v-icon>
                    </template>
                </v-text-field>
            </template>
            <v-date-picker
                v-if="!readOnly"
                class="ml-4"
                :model-value="modelValue"
                :min="projektfeld.value_from"
                :max="projektfeld.value_to"
                first-day-of-week="1"
                :readonly="readOnly"
                @update:model-value="valueChanged"
            ></v-date-picker>
        </v-menu>
    </div>
</template>

<style lang="css" scoped>
.inputfield {
    margin-left: -1rem;
}
.icon-margin {
    margin-left: 0.25rem;
}
</style>
